import { logError } from '@mop/shared/utils/logger';
import { isClient } from '@mop/shared/utils/util';
import { countryConfigModel } from '@/models';
import { createApiCms, createApiAws } from '@/api';
import { navigationListModel } from '@/models/navigationModel';
import { routeChangeMiddleware } from '@/middleware/routeChange';

export default defineNuxtPlugin(async (nuxtApp) => {
  const app = nuxtApp.vueApp.$nuxt;
  if (!app.$mopI18n) {
    return;
  }
  const isStoryblokLivePreview: boolean = app.$storyblokLivePreview.isEnabled;
  const route = useRoute();
  const config = app.$config;

  function getCmsLanguages(): { cmsLanguage: string; cmsFallbackLanguage: string } {
    const STORYBLOK_DEFAULT_PARAMETER_LANGUAGE_KEY = 'default';
    try {
      let cmsLanguage = app.$mopI18n.locale;
      let cmsFallbackLanguage = app.$mopI18n.cmsFallbackLanguage || STORYBLOK_DEFAULT_PARAMETER_LANGUAGE_KEY;
      const storyblokLanguageParameter: string = route.query._storyblok_lang as string;

      if (storyblokLanguageParameter) {
        cmsLanguage = storyblokLanguageParameter;
        if (cmsLanguage === STORYBLOK_DEFAULT_PARAMETER_LANGUAGE_KEY) {
          cmsLanguage = config.public.DEFAULT_STORYBLOK_LANGUAGE;
          cmsFallbackLanguage = '';
        }
      }

      if (cmsLanguage === cmsFallbackLanguage) {
        cmsFallbackLanguage = '';
      }

      return {
        cmsLanguage,
        cmsFallbackLanguage,
      };
    } catch (error) {
      logError(error);
    }
    return {
      cmsLanguage: app.$mopI18n.locale,
      cmsFallbackLanguage: STORYBLOK_DEFAULT_PARAMETER_LANGUAGE_KEY,
    };
  }

  const { cmsLanguage, cmsFallbackLanguage } = getCmsLanguages();
  const apiCms = createApiCms({
    cmsApiKey: isStoryblokLivePreview
      ? config.public.STORYBLOK_API_KEY_PREVIEW
      : config.public.STORYBLOK_API_KEY_PUBLISHED,
    cmsVersion: isStoryblokLivePreview ? 'draft' : 'published',
    cmsLanguage,
    cmsFallbackLanguage,
    cmsRelease: route.query._storyblok_release as string,
  });
  nuxtApp.provide('apiCms', apiCms);
  nuxtApp.provide(
    'apiCompanyAws',
    createApiAws({
      url: config.public.AWS_URL,
    }),
  );

  const { getCmsStory, cmsStoryModelRef: globalConfig } = useMopCms('global');
  // Always use fresh cache version with cv=1 for global story, in order to get correct cv value for other stories
  await getCmsStory('/global', { cv: 1 });
  nuxtApp.provide('mopConfig', countryConfigModel(globalConfig.value.getAttribute('config')?.[0]));
  nuxtApp.provide('navigationList', navigationListModel(globalConfig.value.getAttribute('navigation')).getList());
  nuxtApp.provide(
    'footerNavigationList',
    navigationListModel(globalConfig.value.getAttribute('footer_navigation')).getList(),
  );
  nuxtApp.provide('socialLinkList', navigationListModel(globalConfig.value.getAttribute('social_links')).getList());

  if (isClient) {
    window.getNuxtPayload = () => nuxtApp.payload;
    addRouteMiddleware(async (to) => {
      await routeChangeMiddleware(to);
    });
  }

  // TODO: uncomment when migrated
  // usePrivacy(middlewareInstance).initCookies();
});
